import { useTranslation } from 'react-i18next';
import { DepositCardForm } from './DepositCardForm';
import { type DepositCardFormValues, useDepositCardFormLogic } from '../model';
import { Button, ModalComponentWrapper } from '@/shared/components';
import { cn } from '@/shared/utils';
import type { DepositModalBaseProps } from '@/shared/types';
import { useAppSettingsDictionary } from '@/shared/api';

interface DepositCardModalProps extends DepositModalBaseProps {
  onClose: () => void;
  onSubmit?: (data: DepositCardFormValues) => void;
  onSuccess?: (data: { currencyCode: string; amount: string }) => void;
  onError?: (error: Error) => void;
  assetCode?: string;
}

function CardModalFooter({
  onSubmit,
  isLoading,
}: {
  onSubmit: () => void;
  isLoading: boolean;
}) {
  const { t } = useTranslation();

  return (
    <Button
      type="submit"
      variant="default"
      size="lg"
      disabled={isLoading}
      loading={isLoading}
      onClick={onSubmit}
      className={cn('w-full')}>
      {t('depositMethods.creditCard.submitButton')}
    </Button>
  );
}

export function DepositCardModal({
  onBack,
  onClose,
  title,
  onSubmit,
  onError,
  assetCode,
}: DepositCardModalProps) {
  const {
    form,
    handleFormSubmit,
    currencyOptions,
    minAmount,
    amountPlaceholder,
    commissionData,
    shouldShowCommission,
    isSubmitInProgressWithoutErrors,
    formError,
    showResultModal,
    isSuccess,
    isCheckingStatus,
    operationDetails,
    isLoading,
    isAccountInfoError,
    accountsInfoError,
    hasDataError,
    firstError,
    resultModalClick,
  } = useDepositCardFormLogic({
    initialData: {
      currencyCode: assetCode ?? '',
      amount: '',
    },
    onSubmit,
    onSuccess: () => onClose(),
    onError,
  });

  const { appSettingsData } = useAppSettingsDictionary();

  const submitForm = () => {
    form.handleSubmit(handleFormSubmit)();
  };

  return (
    <ModalComponentWrapper
      title={title}
      onBack={onBack}
      footer={
        showResultModal ? null : (
          <CardModalFooter
            onSubmit={submitForm}
            isLoading={isSubmitInProgressWithoutErrors}
          />
        )
      }>
      <DepositCardForm
        form={form}
        handleFormSubmit={handleFormSubmit}
        onCancel={onBack}
        currencyOptions={currencyOptions}
        minAmount={minAmount}
        amountPlaceholder={amountPlaceholder}
        commissionData={commissionData}
        shouldShowCommission={shouldShowCommission}
        isSubmitInProgressWithoutErrors={isSubmitInProgressWithoutErrors}
        formError={formError}
        showResultModal={showResultModal}
        bottomSheetText={
          appSettingsData?.value?.depositWithdrawInformation?.displayType !=
          'none'
            ? appSettingsData?.value?.depositWithdrawInformation
                ?.bottomsheetText
            : undefined
        }
        isSuccess={isSuccess}
        isCheckingStatus={isCheckingStatus}
        operationDetails={operationDetails}
        isLoading={isLoading}
        isAccountInfoError={isAccountInfoError}
        accountsInfoError={accountsInfoError?.message || null}
        hasDataError={hasDataError}
        firstError={firstError?.message || null}
        resultModalClick={resultModalClick}
      />
    </ModalComponentWrapper>
  );
}
